import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, Calendar } from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";

import { getBlogBySlug } from "../firebase/blogService";

import "./BlogArticle.css";

const BlogArticle = () => {

    const { slug } = useParams();

    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);


    /* =========================================
       LOAD BLOG
    ========================================= */

    useEffect(() => {

        const loadBlog = async () => {

            try {

                const data = await getBlogBySlug(slug);

                setBlog(data);

            } catch (error) {

                console.error("Error loading blog:", error);

            } finally {

                setLoading(false);

            }

        };

        loadBlog();

    }, [slug]);


    /* =========================================
       DYNAMIC SEO METADATA
    ========================================= */

    useEffect(() => {

        if (!blog) return;


        const seoTitle =
            blog.seoTitle ||
            blog.title ||
            "Shirdhankar Technologies Blog";


        const seoDescription =
            blog.seoDescription ||
            blog.summary ||
            "Insights, guides and articles from Shirdhankar Technologies.";


        const canonicalUrl =
            `https://shirdhankartechnologies.com/blog/${blog.slug || slug}`;


        const imageUrl =
            blog.heroImage ||
            blog.cardImage ||
            "";


        /* -----------------------------------------
           PAGE TITLE
        ----------------------------------------- */

        document.title = `${seoTitle} | Shirdhankar Technologies`;


        /* -----------------------------------------
           HELPER
        ----------------------------------------- */

        const setMeta = (attribute, key, content) => {

            if (!content) return;

            let element = document.head.querySelector(
                `meta[${attribute}="${key}"]`
            );

            if (!element) {

                element = document.createElement("meta");

                element.setAttribute(attribute, key);

                document.head.appendChild(element);

            }

            element.setAttribute("content", content);

        };


        /* -----------------------------------------
           STANDARD SEO
        ----------------------------------------- */

        setMeta(
            "name",
            "description",
            seoDescription
        );


        /* -----------------------------------------
           OPEN GRAPH
        ----------------------------------------- */

        setMeta(
            "property",
            "og:title",
            seoTitle
        );


        setMeta(
            "property",
            "og:description",
            seoDescription
        );


        setMeta(
            "property",
            "og:type",
            "article"
        );


        setMeta(
            "property",
            "og:url",
            canonicalUrl
        );


        setMeta(
            "property",
            "og:image",
            imageUrl
        );


        /* -----------------------------------------
           TWITTER
        ----------------------------------------- */

        setMeta(
            "name",
            "twitter:card",
            "summary_large_image"
        );


        setMeta(
            "name",
            "twitter:title",
            seoTitle
        );


        setMeta(
            "name",
            "twitter:description",
            seoDescription
        );


        setMeta(
            "name",
            "twitter:image",
            imageUrl
        );


        /* -----------------------------------------
           CANONICAL URL
        ----------------------------------------- */

        let canonical =
            document.head.querySelector(
                'link[rel="canonical"]'
            );


        if (!canonical) {

            canonical = document.createElement("link");

            canonical.setAttribute(
                "rel",
                "canonical"
            );

            document.head.appendChild(canonical);

        }


        canonical.setAttribute(
            "href",
            canonicalUrl
        );


    }, [blog, slug]);


    /* =========================================
       LOADING
    ========================================= */

    if (loading) {

        return (
            <>
                <Navbar />

                <main className="blog-article-page">

                    <div
                        style={{
                            minHeight: "70vh",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff"
                        }}
                    >
                        Loading Article...
                    </div>

                </main>

                <Footer />

                <WhatsAppButton />
            </>
        );

    }


    /* =========================================
       ARTICLE NOT FOUND
    ========================================= */

    if (!blog) {

        return (
            <>
                <Navbar />

                <main className="blog-article-page">

                    <div
                        style={{
                            minHeight: "70vh",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: "40px"
                        }}
                    >

                        <h1>Article Not Found</h1>

                        <p
                            style={{
                                color: "#8f9bb5"
                            }}
                        >
                            The article you're looking for doesn't exist.
                        </p>

                        <Link
                            to="/blog"
                            style={{
                                marginTop: "20px",
                                color: "#19d8ff",
                                textDecoration: "none",
                                fontWeight: "600"
                            }}
                        >
                            ← Back to Blog
                        </Link>

                    </div>

                </main>

                <Footer />

                <WhatsAppButton />
            </>
        );

    }


    /* =========================================
       ARTICLE PAGE
    ========================================= */

    return (
        <>

            <Navbar />

            <main className="blog-article-page">


                {/* =========================================
                    ARTICLE HEADER
                ========================================= */}

                <header className="article-header">

                    <div className="article-breadcrumb">

                        <Link to="/blog">
                            Blog
                        </Link>

                        <span>/</span>

                        <span>
                            Articles
                        </span>

                    </div>


                    <div className="article-category">

                        {blog.category}

                    </div>


                    <h1 className="article-title">

                        {blog.title}

                    </h1>


                    <div className="article-meta">

                        <span>

                            <Clock size={13} />

                            {blog.readTime}

                        </span>


                        <span>

                            <Calendar size={13} />

                            {blog.date}

                        </span>

                    </div>

                </header>


                {/* =========================================
                    HERO IMAGE
                ========================================= */}

                <div className="article-hero-wrapper">

                    <img
                        className="article-hero"
                        src={blog.heroImage}
                        alt={blog.title}
                    />

                </div>


                {/* =========================================
                    ARTICLE CONTENT
                ========================================= */}

                <article className="article-content">

                    <div className="article-content-text">

                        {blog.content}

                    </div>

                </article>


                {/* =========================================
                    CTA
                ========================================= */}

                <section className="article-cta">

                    <div className="article-cta-label">

                        • LET'S BUILD TOGETHER •

                    </div>


                    <h2>

                        Ready To Build Your Next{" "}

                        <span>
                            Digital Product?
                        </span>

                    </h2>


                    <p>

                        Let's discuss your project requirements,
                        timeline, and budget. Our team is ready to
                        turn your vision into reality.

                    </p>


                    <div className="article-cta-buttons">

                        <Link
                            to="/#contact"
                            className="article-cta-primary"
                        >
                            Get Free Consultation →
                        </Link>


                        <Link
                            to="/#contact"
                            className="article-cta-secondary"
                        >
                            Contact Us
                        </Link>

                    </div>

                </section>


            </main>


            <Footer />

            <WhatsAppButton />

        </>
    );

};

export default BlogArticle;